import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, CheckCircle2, BookOpen } from "lucide-react";

const INTROS = {
  "Foundations": "The bedrock: mathematical language, sets, logic, functions and proof. Start here to read and write mathematics with precision.",
  "Algebra": "From ratios and factoring to sequences and series — the manipulation of structure and symbol.",
  "Geometry": "Shape, distance and transformation, from Euclid to coordinate and non-Euclidean geometry.",
  "Trigonometry": "Angles, identities and the circular/exponential bridge between geometry and analysis.",
  "Calculus": "Limits, derivatives and integrals — the mathematics of change and accumulation.",
  "Linear Algebra": "Vectors, matrices, determinants and spectra — the language of linear structure.",
  "Differential Equations": "Modelling dynamics: ODEs, PDEs, stability and boundary-value problems.",
  "Probability & Statistics": "Uncertainty, distributions and inference — reasoning from data.",
  "Discrete & CS": "Graphs, combinatorics, logic circuits, automata and complexity.",
  "Number Theory": "Divisibility, residues, Diophantine equations and the deep structure of the integers.",
  "Analysis & Topology": "Rigour at the limit: metric spaces, convergence, measure, complex analysis and topology.",
  "Optimization & Numerical": "Finding the best and computing it well — optimisation, numerical methods and error control.",
  "Applied & Physics": "Mathematics in the world: mechanics, thermodynamics, signals, finance and modelling.",
  "AI & Verification": "How machine and human reasoning are checked — benchmarks, formalisation and verification.",
  "Platform & Pedagogy": "How Axioms teaches, commissions, reviews and rewards mathematical work.",
};

export default function ShelfLanding() {
  const { subject } = useParams();
  const { user } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [done, setDone] = useState(new Set());

  useEffect(() => {
    api.get("/learn/lessons").then((r) => {
      const ls = r.data.filter((l) => (l.subject || l.domain) === subject).sort((a, b) => (a.order || 0) - (b.order || 0));
      setLessons(ls);
    }).catch(() => {});
    if (user) api.get("/learn/progress").then((r) => setDone(new Set(r.data.completed_lessons || []))).catch(() => {});
  }, [subject, user]);

  const intro = INTROS[subject] || `A curated shelf of ${subject} chapters from the Axioms knowledge base.`;
  const completed = lessons.filter((l) => done.has(l.id)).length;

  return (
    <div className="mx-auto max-w-3xl px-5 py-12 sm:px-8" data-testid="shelf-landing-page">
      <Link to="/learn" className="mb-6 inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> All shelves
      </Link>
      <p className="eyebrow mb-2">Subject shelf</p>
      <h1 className="font-display text-3xl tracking-tight text-slate-900 sm:text-4xl">{subject}</h1>
      <p className="mt-3 text-slate-600">{intro}</p>
      <p className="mt-4 text-sm text-slate-500">{completed}/{lessons.length} chapters complete · a suggested learning path:</p>

      <ol className="mt-6 space-y-3">
        {lessons.map((l, i) => (
          <li key={l.id}>
            <Link to={`/learn/${l.id}`} data-testid={`path-${l.id}`} className="axiom-card flex items-center gap-4 p-4">
              <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full border border-slate-200 font-mono text-sm text-[#E61A27]">{i + 1}</span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-lg text-slate-900">{l.title}</p>
                <p className="truncate text-xs text-slate-500">{l.summary}</p>
              </div>
              {done.has(l.id) ? <CheckCircle2 className="h-5 w-5 flex-none text-emerald-600" /> : <BookOpen className="h-5 w-5 flex-none text-slate-300" />}
            </Link>
          </li>
        ))}
      </ol>
      {lessons.length === 0 && <p className="mt-6 text-slate-500" data-testid="shelf-empty">No chapters on this shelf.</p>}
    </div>
  );
}
